// src/pages/demo.js
import React, { useState, useEffect } from 'react';
import { Box, Button, Input, VStack, useToast, Flex, Select } from '@chakra-ui/react';
import axios from 'axios';
import { BackgroundBeams } from "../ui/background-beams.tsx";
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';

const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// Use prod-friendly base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

// Basic HTML-escape to keep requests safe
const sanitizeInput = (text) =>
  text.replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export default function DemoPage() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [loading, setLoading] = useState(false);

  // Staging feature: regulation + custom "Other"
  const [regulation, setRegulation] = useState('HIPAA');
  const [customRegulation, setCustomRegulation] = useState('');

  const toast = useToast();
  const is_demo = true;

  const frequency = 'instant';
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Keep prod's demo tier unless you want premium
  const tier = 'premium';

  const navigate = useNavigate();

  // Auth check + token verification (keep prod behavior)
  useEffect(() => {
    const token = localStorage.getItem('demoAuthToken');
    if (!token) {
      navigate('/login');
      toast({
        title: 'Authentication Required',
        description: 'Please log in to access the demo.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
    } else {
      const verifyToken = async () => {
        try {
          await axios.get(`${API_BASE_URL}/test_auth`, {
            headers: { Authorization: `Bearer ${token}` },
          });
        } catch (err) {
          if (err.response && err.response.status === 401) {
            localStorage.removeItem('demoAuthToken');
            navigate('/login');
            toast({
              title: 'Session Expired',
              description: 'Please log in again.',
              status: 'error',
              duration: 3000,
              isClosable: true,
            });
          } else {
            console.error('Error verifying token:', err);
          }
        }
      };
      verifyToken();
    }
  }, [navigate, toast]);

  const handleSubmit = async () => {
    const sanitizedEmail = sanitizeInput(email);
    const sanitizedName = sanitizeInput(name);
    const sanitizedSurname = sanitizeInput(surname);

    if (!sanitizedEmail || !validateEmail(sanitizedEmail) || !sanitizedName || !sanitizedSurname) {
      toast({
        title: 'Error',
        description: 'Please provide a valid email, name, and surname.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const token = localStorage.getItem('demoAuthToken');
    if (!token) {
      navigate('/login');
      toast({
        title: 'Error',
        description: 'Not authenticated. Please log in.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Merge-in staging’s “Other” handling with strict validation
    let rawReg = regulation === 'OTHER' ? customRegulation : regulation;
    if (regulation === 'OTHER') {
      const cleaned = (rawReg || '').trim();
      const ok =
        cleaned.length >= 2 &&
        cleaned.length <= 150 &&
        /^[\w\s().,:&/'-]+$/.test(cleaned);
      if (!ok) {
        toast({
          title: 'Invalid regulation',
          description:
            "Enter 2–150 chars. Allowed: letters, numbers, spaces, and . , : ( ) & / ' -",
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      rawReg = cleaned.replace(/\s+/g, ' ');
    }
    const safeRegulation = sanitizeInput(rawReg);

    try {
      setLoading(true);

      await axios.post(
        `${API_BASE_URL}/process_request`,
        {
          email: sanitizedEmail,
          regulation: safeRegulation, // send derived/validated value
          frequency,
          timezone,
          is_demo,
          tier,
          name: sanitizedName,
          surname: sanitizedSurname,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast({
        title: 'Email Sent',
        description: `${safeRegulation} information has been sent to your inbox.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setEmail('');
      setName('');
      setSurname('');
      if (regulation === 'OTHER') setCustomRegulation('');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Keep prod’s logout API but move to fixed top-right (staging UX)
  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('demoAuthToken');
      if (token) {
        await axios.post(
          `${API_BASE_URL}/logout`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
    } catch (error) {
      console.error('Error during logout API call:', error);
    } finally {
      localStorage.removeItem('demoAuthToken');
      navigate('/login');
      toast({
        title: 'Logged Out',
        description: 'You have been successfully logged out.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box py={12} px={6} position="relative" height="100vh">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Demo | Compliance Agent</title>
      </Helmet>

      <BackgroundBeams />

      {/* Staging-style fixed logout button */}
      <Box position="fixed" top={4} right={4} zIndex={20}>
        <Button
          variant="solid"
          colorScheme="red"
          borderRadius="full"
          size="md"
          onClick={handleLogout}
        >
          Log out
        </Button>
      </Box>

      <Flex
        direction="column"
        justify="center"
        align="center"
        height="100%"
        position="absolute"
        top="-100px"          // staging fix: valid CSS unit
        left="0"
        width="100%"
        zIndex={1}
      >
        <VStack spacing={4} align="center">
          <Input
            placeholder="Enter your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            width="100%"
            maxWidth="1000px"
            size="md"
            bg="white"
            border="1px solid #ccc"
            borderRadius="full"
            _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px #3182ce' }}
          />
          <Input
            placeholder="Enter your surname"
            value={surname}
            onChange={(e) => setSurname(e.target.value)}
            width="100%"
            maxWidth="1000px"
            size="md"
            bg="white"
            border="1px solid #ccc"
            borderRadius="full"
            _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px #3182ce' }}
          />
          <Input
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            width="100%"
            maxWidth="1000px"
            size="md"
            bg="white"
            border="1px solid #ccc"
            borderRadius="full"
            _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px #3182ce' }}
          />

          {/* Regulation Dropdown with "Other" option (from staging) */}
          <Select
            placeholder="Select Regulation"
            value={regulation}
            onChange={(e) => {
              setRegulation(e.target.value);
              if (e.target.value !== 'OTHER') setCustomRegulation('');
            }}
            width="100%"
            maxWidth="1000px"
            size="md"
            bg="white"
            border="1px solid #ccc"
            borderRadius="full"
            _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px #3182ce' }}
          >
            <option value="HIPAA">HIPAA</option>
            <option value="POPIA">POPIA</option>
            <option value="GDPR (UK)">GDPR (UK)</option>
            <option value="GDPR (EU)">GDPR (EU)</option>
            <option value="OTHER">Other (Please Specify)</option>
          </Select>

          {/* Only show when "Other" is selected */}
          {regulation === 'OTHER' && (
            <Input
              placeholder="Type the regulation (e.g., LGPD, CCPA, PDPA)"
              value={customRegulation}
              onChange={(e) => setCustomRegulation(e.target.value)}
              width="100%"
              maxWidth="1000px"
              size="md"
              bg="white"
              border="1px solid #ccc"
              borderRadius="full"
              maxLength={150}
              _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px #3182ce' }}
              autoFocus
            />
          )}

          <Button
            colorScheme="green"
            onClick={handleSubmit}
            isLoading={loading}
            isDisabled={regulation === 'OTHER' && !customRegulation.trim()}
            loadingText="Sending Email"
            borderRadius="full"
          >
            Get The Latest{' '}
            {regulation === 'OTHER' && customRegulation.trim()
              ? customRegulation.trim()
              : regulation}{' '}
            Information
          </Button>
        </VStack>
      </Flex>
    </Box>
  );
}
